const D2R = Math.PI / 180;

export type GlobeRotation = {
  lambda: number;
  phi: number;
  gamma: number;
};

export type GlobeMarker = {
  label: string;
  latitude: number;
  longitude: number;
  color?: string;
};

export type ProjectedPoint = {
  sx: number;
  sy: number;
  visible: boolean;
};

/** Match TacticalGlobe3D orthographic projection. */
export function projectLngLat(
  lng: number,
  lat: number,
  rotation: GlobeRotation,
  radius: number,
  cx: number,
  cy: number,
): ProjectedPoint {
  const lr = (lng - rotation.lambda) * D2R;
  const la = lat * D2R;
  const cl = Math.cos(la);
  const x0 = cl * Math.cos(lr);
  const y0 = cl * Math.sin(lr);
  const z0 = Math.sin(la);

  const cp = Math.cos(rotation.phi * D2R);
  const sp = Math.sin(rotation.phi * D2R);
  const x1 = x0 * cp + z0 * sp;
  const y1 = y0;
  const z1 = -x0 * sp + z0 * cp;

  const cg = Math.cos(rotation.gamma * D2R);
  const sg = Math.sin(rotation.gamma * D2R);
  const rx = x1;
  const ry = y1 * cg - z1 * sg;
  const rz = y1 * sg + z1 * cg;

  return {
    sx: cx + radius * ry,
    sy: cy - radius * rz,
    visible: rx >= 0,
  };
}

/** Same layout math as TacticalGlobe3D (padding 8). */
export function globeLayout(width: number, height: number) {
  const padding = 8;
  const innerW = Math.max(0, width - padding * 2);
  const innerH = Math.max(0, height - padding * 2);
  const radius = Math.max(20, Math.min(innerW, innerH) / 2 - 8);
  return {
    cx: width / 2,
    cy: height / 2,
    radius,
    clipRadius: radius,
  };
}

export function buildMarkerArcPath(
  from: ProjectedPoint,
  to: ProjectedPoint,
  cx: number,
  cy: number,
): string {
  const mx = (from.sx + to.sx) / 2;
  const my = (from.sy + to.sy) / 2;
  const dx = mx - cx;
  const dy = my - cy;
  const dist = Math.hypot(dx, dy) || 1;
  const span = Math.hypot(to.sx - from.sx, to.sy - from.sy);
  const bulge = span * 0.28;
  const qx = mx + (dx / dist) * bulge;
  const qy = my + (dy / dist) * bulge;
  return `M ${from.sx.toFixed(1)} ${from.sy.toFixed(1)} Q ${qx.toFixed(1)} ${qy.toFixed(1)} ${to.sx.toFixed(1)} ${to.sy.toFixed(1)}`;
}
